import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import * as React from "react";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { useUpdateLayout } from "@/shared/domain/layouts/hooks";
import { LayoutRecord } from "@/shared/domain/layouts/LayoutRecord";

import { FolderNameInput } from "./FolderNameInput";
import { LayoutFolderGroup } from "./layoutFolders";
import { useBulkLayoutMutation } from "./useBulkLayoutMutation";

interface RenameFolderModalProps {
  group: LayoutFolderGroup | null;
  existingFolders: string[];
  isOpen: boolean;
  onClose: () => void;
  onExited: () => void;
  onSuccess: (updated: LayoutRecord[]) => void;
}

export function RenameFolderModal({
  group,
  existingFolders,
  isOpen,
  onClose,
  onExited,
  onSuccess,
}: RenameFolderModalProps) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionProps={{ onExited }}
      aria-labelledby="rename-folder-dialog-title"
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      {group && group.folder !== null && (
        <RenameFolderForm
          group={group}
          currentName={group.folder}
          existingFolders={existingFolders}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
    </Dialog>
  );
}

interface RenameFolderFormProps {
  group: LayoutFolderGroup;
  currentName: string;
  existingFolders: string[];
  onClose: () => void;
  onSuccess: (updated: LayoutRecord[]) => void;
}

function RenameFolderForm({
  group,
  currentName,
  existingFolders,
  onClose,
  onSuccess,
}: RenameFolderFormProps) {
  const [newName, setNewName] = React.useState(currentName);
  const updateLayout = useUpdateLayout();
  const { run, isPending, errorMessage, setErrorMessage } =
    useBulkLayoutMutation("Failed to rename folder. Please try again.");

  const trimmed = newName.trim();
  const count = group.layouts.length;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!trimmed) {
      setErrorMessage("Folder name is required.");
      return;
    }
    run(
      group.layouts,
      (option) =>
        updateLayout.mutateAsync({
          layoutId: option.id,
          updates: { folder: trimmed },
        }),
      () =>
        onSuccess(
          group.layouts.map((option) => ({
            ...option.record,
            folder: trimmed,
          })),
        ),
    );
  };

  const suggestions = existingFolders.filter((name) => name !== currentName);

  return (
    <form onSubmit={onSubmit}>
      <DialogTitle id="rename-folder-dialog-title" sx={{ pr: 6 }}>
        Rename folder
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body1">
            Rename <b>&quot;{currentName}&quot;</b> ({count}{" "}
            {count === 1 ? "layout" : "layouts"}). Renaming onto an existing
            folder merges them.
          </Typography>

          <FolderNameInput
            value={newName}
            onChange={setNewName}
            existingFolders={suggestions}
            label="Folder name"
            autoFocus
            disabled={isPending}
          />

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <RobotoButton
          eventName="RenameFolderCanceled"
          eventProperties={{ folder: currentName }}
          onClick={onClose}
          type="button"
          disabled={isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="RenameFolderClicked"
          eventProperties={() => ({ folder: currentName, newFolder: trimmed })}
          type="submit"
          variant="contained"
          disabled={isPending || trimmed === "" || trimmed === currentName}
          endIcon={
            isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Rename
        </RobotoButton>
      </DialogActions>
    </form>
  );
}
